/**
 * Cookie-consent state + Google Consent Mode v2 plumbing for the storefronts.
 *
 * UK PECR requires prior consent for non-essential (analytics) cookies, so the
 * GTM/GA4 container must not load until the visitor accepts. The flow is
 * "basic" Consent Mode v2: consent defaults are pushed as DENIED before any
 * Google script could run, and the GoogleTagManager tag itself is only
 * rendered once the stored choice is "granted"
 * (see components/cookie-consent.tsx). Essential storage — the Snipcart
 * basket in localStorage and Firebase Auth persistence — is strictly
 * necessary and unaffected by the choice.
 *
 * The choice persists in localStorage and is broadcast via window events so
 * the banner, the GTM gate, and the footer "Cookie preferences" reprompt all
 * stay in sync without a context provider.
 */

export type CookieConsentChoice = "granted" | "denied";

const STORAGE_KEY = "rl-cookie-consent.v1";
const CHANGE_EVENT = "rl-cookie-consent-changed";
const REPROMPT_EVENT = "rl-cookie-consent-reprompt";

export function readStoredCookieConsent(): CookieConsentChoice | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    // Storage unavailable (private mode / blocked) — treat as undecided so
    // analytics stays off.
    return null;
  }
}

export function storeCookieConsent(choice: CookieConsentChoice) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Persisting failed; the in-memory choice still applies for this visit.
  }

  window.dispatchEvent(new CustomEvent<CookieConsentChoice>(CHANGE_EVENT, { detail: choice }));
}

export function subscribeToCookieConsent(
  listener: (choice: CookieConsentChoice) => void,
) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<CookieConsentChoice>).detail;
    if (detail === "granted" || detail === "denied") {
      listener(detail);
    }
  };

  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Re-opens the consent banner (used by the footer "Cookie preferences" link). */
export function requestCookieConsentPrompt() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(REPROMPT_EVENT));
}

export function subscribeToCookieConsentPrompt(listener: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(REPROMPT_EVENT, listener);
  return () => window.removeEventListener(REPROMPT_EVENT, listener);
}

/**
 * Google's gtag commands must be pushed as an `arguments` object — pushing a
 * plain array is not reliably interpreted by GTM, so this mirrors the
 * official snippet exactly.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * Push the Consent Mode v2 defaults (everything denied). Must run before the
 * GTM script could evaluate; GTM replays the queued dataLayer in order.
 */
export function applyAnalyticsConsentDefaultDenied() {
  if (typeof window === "undefined") {
    return;
  }

  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
}

/** Grant analytics storage only — the storefronts run no advertising tags. */
export function applyAnalyticsConsentGranted() {
  if (typeof window === "undefined") {
    return;
  }

  gtag("consent", "update", { analytics_storage: "granted" });
}

/**
 * Re-deny analytics storage. Needed for the decline-after-grant path (footer
 * "Cookie preferences" → Essential only): the GTM script is already loaded
 * and can't be unloaded mid-session, so the consent state itself must flip to
 * denied to stop further cookie writes immediately.
 */
export function applyAnalyticsConsentDenied() {
  if (typeof window === "undefined") {
    return;
  }

  gtag("consent", "update", { analytics_storage: "denied" });
}

/**
 * Best-effort removal of Google Analytics cookies after a visitor declines
 * (relevant when they previously accepted and changed their mind via the
 * footer reprompt). GA cookies are host- or registrable-domain-scoped, so
 * expire each `_ga*` cookie against every plausible domain attribute.
 */
export function clearAnalyticsCookies() {
  if (typeof document === "undefined") {
    return;
  }

  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const hostname = window.location.hostname;
  const domainParts = hostname.split(".");
  const domains = [null, hostname];
  for (let index = 0; index < domainParts.length - 1; index += 1) {
    domains.push(`.${domainParts.slice(index).join(".")}`);
  }

  for (const rawCookie of document.cookie.split(";")) {
    const name = rawCookie.split("=")[0]?.trim();
    if (!name || !/^(_ga|_gid|_gat)/.test(name)) {
      continue;
    }

    for (const domain of domains) {
      document.cookie = `${name}=; ${expiry}; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
}
